export class SDKError extends Error {
  constructor(
    public group: string,
    public code: string,
    message: string,
    public data?: any,
  ) {
    super(message);
    this.name = "SDKError";
  }

  toJSON() {
    return {
      name: this.name,
      group: this.group,
      code: this.code,
      message: this.message,
      data: this.data,
    };
  }
}

// Middle layer for player-related errors
export class PlayerError extends SDKError {
  constructor(group: string, code: string, message: string, data?: any) {
    super(group, code, message, data);
    this.name = "PlayerError";
  }
}

// Middle layer for API-related errors
export class ApiError extends SDKError {
  constructor(code: string, message: string, data?: any) {
    super("ApiError", code, message, data);
    this.name = "ApiError";
  }
}

export const StreamMetadataError = {
  StreamUnavailable: new PlayerError(
    "StreamMetadataError",
    "StreamUnavailable",
    "The requested stream is currently unavailable.",
  ),
  StreamInvalid: new PlayerError(
    "StreamMetadataError",
    "StreamInvalid",
    "The stream has been invalidated and can no longer be used.",
  ),
  MixIsInactive: new PlayerError(
    "StreamMetadataError",
    "MixIsInactive",
    "The mix is currently inactive.",
  ),
};

export const StreamSystemError = {
  ScreenCaptureActive: new PlayerError(
    "StreamSystemError",
    "ScreenCaptureActive",
    "Screen capture is active, which is not permitted.",
  ),
  RecordPermissionUndetermined: new PlayerError(
    "StreamSystemError",
    "RecordPermissionUndetermined",
    "Recording permission has not been determined.",
  ),
  RecordPermissionDenied: new PlayerError(
    "StreamSystemError",
    "RecordPermissionDenied",
    "Recording permission was denied.",
  ),
  AudioOutputPortForbidden: new PlayerError(
    "StreamSystemError",
    "AudioOutputPortForbidden",
    "The current audio output port is not allowed.",
  ),
};

export const NetworkError = {
  UserDeniedWifiConnect: new PlayerError(
    "NetworkError",
    "UserDeniedWifiConnect",
    "The user denied the request to join the network.",
  ),
  WifiConnectFailed: new PlayerError(
    "NetworkError",
    "WifiConnectFailed",
    "Failed to connect to the WiFi network.",
  ),
  NetworkInterfaceBindingFailed: new PlayerError(
    "NetworkError",
    "NetworkInterfaceBindingFailed",
    "Failed to bind to the specified network interface.",
  ),
};

export const AnyPlayerError = (
  group: string,
  code: string,
  message: string,
  data?: any,
) => new PlayerError(group, code, message, data);
export const UnknownPlayerError = new PlayerError(
  "PlayerError",
  "Unknown",
  "An unknown player error occurred.",
);

export const AudioDecoderError = {
  OpusDecoderInitializationFailed: new PlayerError(
    "AudioDecoderError",
    "OpusDecoderInitializationFailed",
    "Failed to initialize the Opus audio decoder.",
  ),
  DecoderNotInitialized: new PlayerError(
    "AudioDecoderError",
    "DecoderNotInitialized",
    "Opus decoder is not initialized.",
  ),
  InvalidOpusFrameHeader: new PlayerError(
    "AudioDecoderError",
    "InvalidOpusFrameHeader",
    "Invalid opus frame header detected.",
  ),
  InvalidOpusFrameLength: new PlayerError(
    "AudioDecoderError",
    "InvalidOpusFrameLength",
    "Invalid opus frame length detected.",
  ),
  HeaderDecodingFailed: new PlayerError(
    "AudioDecoderError",
    "HeaderDecodingFailed",
    "Failed to decode the stream header.",
  ),
  AudioProcessingFailed: new PlayerError(
    "AudioDecoderError",
    "AudioProcessingFailed",
    "Failed to process audio data.",
  ),
  DecoderResetFailed: new PlayerError(
    "AudioDecoderError",
    "DecoderResetFailed",
    "Failed to reset the audio decoder.",
  ),
};

export const WebSocketError = {
  ConnectionFailed: new PlayerError(
    "WebSocketError",
    "ConnectionFailed",
    "Failed to establish WebSocket connection.",
  ),
  ConnectionTimeout: new PlayerError(
    "WebSocketError",
    "ConnectionTimeout",
    "WebSocket connection timed out.",
  ),
  BlobConversionFailed: new PlayerError(
    "WebSocketError",
    "BlobConversionFailed",
    "Failed to convert Blob data to ArrayBuffer.",
  ),
  SubscriptionFailed: new PlayerError(
    "WebSocketError",
    "SubscriptionFailed",
    "Failed to subscribe to the stream.",
  ),
  InvalidWebSocketState: new PlayerError(
    "WebSocketError",
    "InvalidWebSocketState",
    "WebSocket is in an invalid state for the requested operation.",
  ),
};

export const StreamerError = {
  InvalidConfiguration: new PlayerError(
    "StreamerError",
    "InvalidConfiguration",
    "Invalid streamer configuration provided.",
  ),
  QueueOverflow: new PlayerError(
    "StreamerError",
    "QueueOverflow",
    "Stream queue has exceeded maximum capacity.",
  ),
  StreamProcessingFailed: new PlayerError(
    "StreamerError",
    "StreamProcessingFailed",
    "Failed to process stream data.",
  ),
};

export const AudioInitializationError = {
  AudioContextCreationFailed: new PlayerError(
    "AudioInitializationError",
    "AudioContextCreationFailed",
    "Failed to create or initialize the audio context.",
  ),
  WorkletModuleLoadFailed: new PlayerError(
    "AudioInitializationError",
    "WorkletModuleLoadFailed",
    "Failed to load the audio worklet module required for processing.",
  ),
  AudioContextResumeFailed: new PlayerError(
    "AudioInitializationError",
    "AudioContextResumeFailed",
    "Failed to resume the suspended audio context for playback.",
  ),
  AudioElementPlaybackFailed: new PlayerError(
    "AudioInitializationError",
    "AudioElementPlaybackFailed",
    "Failed to play the audio element required for background playback.",
  ),
};

export const BufferProcessingError = {
  WorkletNodeCreationFailed: new PlayerError(
    "BufferProcessingError",
    "WorkletNodeCreationFailed",
    "Failed to create audio worklet node for buffer processing.",
  ),
};

export const ApiErrors = {
  InvalidResponse: new ApiError(
    "InvalidResponse",
    "The API response format is invalid or unexpected.",
  ),
  InvalidToken: new ApiError(
    "InvalidToken",
    "The authentication token is invalid or has expired.",
  ),
  FetchFailed: (message: string, data?: any): ApiError =>
    new ApiError("FetchFailed", message, data),
  Unknown: (message: string): ApiError => new ApiError("UnknownError", message),
  /**
   * Creates an HTTP error for non-success status codes
   * @param statusCode The HTTP status code received
   * @param message The error message from the response
   */
  HttpError: (statusCode: number, message: string): ApiError =>
    new ApiError("HttpError", message, { statusCode }),
};

/**
 * Generic function to attach additional data to any error
 * @param error The base error object
 * @param data Additional data to attach
 * @returns A new error object with the attached data
 */
export function withErrorData(error: SDKError, data: any): SDKError {
  const newError = new (error.constructor as any)(
    error.group,
    error.code,
    error.message,
    {
      ...error.data,
      ...data,
    },
  );
  return newError;
}

/**
 * Helper function to create an unknown error with additional context data
 */
export function createUnknownError(
  originalError?: any,
  additionalData?: any,
): SDKError {
  // Extract the original error message if available
  let errorMessage = UnknownPlayerError.message;
  let originalStack: string | undefined;

  if (originalError) {
    const originalMessage =
      originalError instanceof Error
        ? originalError.message
        : typeof originalError === "string"
          ? originalError
          : originalError?.message || String(originalError);

    if (originalMessage) {
      errorMessage = `${UnknownPlayerError.message}: ${originalMessage}`;
    }

    // Capture stack trace if available
    if (originalError instanceof Error && originalError.stack) {
      originalStack = originalError.stack;
    } else if (originalError?.stack) {
      originalStack = originalError.stack;
    }
  }

  // Create a new error with the enhanced message
  const enhancedError = new PlayerError(
    UnknownPlayerError.group,
    UnknownPlayerError.code,
    errorMessage,
    {
      originalError: originalError
        ? {
            message:
              originalError instanceof Error
                ? originalError.message
                : typeof originalError === "string"
                  ? originalError
                  : originalError?.message,
            stack: originalStack,
            name:
              originalError instanceof Error ? originalError.name : undefined,
            // Include the full error object for debugging
            fullError: originalError,
          }
        : undefined,
      additionalData,
      timestamp: new Date().toISOString(),
    },
  );

  // Preserve the original stack trace if available
  if (originalStack) {
    // Create a new Error to capture the current stack
    const stackError = new Error();
    if (stackError.stack) {
      // Combine the original stack with the current location
      enhancedError.stack = `${originalStack}\n---\n${stackError.stack}`;
    }
  }

  return enhancedError;
}
